import { ExerciseType } from "../types/exercise";
import api from "./api";

export interface Exercise {
  id: number;
  name: string;
  description: string;
  imageUrls: string[];
  videoUrls: {
    url: string;
    thumbnail: string;
  }[];
  workingSets: {
    set: number;
    kg?: string;
    reps?: string;
    time?: string;
    meters?: string;
    kilometers?: string;
    minutes?: string;
  }[];
  fitnessLevel: string[];
  priority: string;
  fitnessGoal: string[];
  exerciseType:
    | "Clear"
    | "Time-based"
    | "Weight and reps-based"
    | "Distance-based"
    | "cardio";
  gender: string[];
  mood: string[];
  programIds?: number[];
  categoryIds?: number[];
  equipmentIds?: number[];
  primaryTargetMuscles: {
    id: number;
    name: string;
    imageUrl: string;
  }[];
  secondaryTargetMuscles: {
    id: number;
    name: string;
    imageUrl: string;
  }[];
  programs: {
    id: number;
    title: string;
    imageUrl: string;
    trainer: string;
  }[];
  categories: {
    title: string;
    id: number;
    name: string;
    imageUrl: string;
  }[];
  equipment: any[];
  howToPerformIds?: number[];
  howToPerform: {
    id: number;
    name: string;
    description?: string;
    imageUrls?: string[];
    videoUrls?: { url: string; thumbnail?: string }[];
  }[];
}

interface ExerciseResponse {
  data: Exercise[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const getExercises = async (
  page: number = 1,
  limit: number = 10,
  name: string[] = [],
  categoryName: string[] = [],
  programName: string[] = [],
  targetMuscleName: string[] = []
): Promise<ExerciseResponse> => {
  try {
    const params = {
      page,
      limit,
      ...(name.length > 0 && { "name[]": name }),
      ...(categoryName.length > 0 && { "categoryName[]": categoryName }),
      ...(programName.length > 0 && { "programName[]": programName }),
      ...(targetMuscleName.length > 0 && {
        "targetMuscleName[]": targetMuscleName,
      }),
    };

    console.log("Params:", params);

    const response = await api.get<ExerciseResponse>(`/exercises`, {
      params,
      timeout: 0,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach((key) => {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(`${key}[]`, v));
          } else {
            searchParams.append(key, value);
          }
        });
        return searchParams.toString();
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching exercises:", error);
    throw error;
  }
};

export const searchExercises = async (
  searchType: "name" | "categoryName" | "programName" | "targetMuscleName",
  query: string
): Promise<string[]> => {
  if (!query.trim()) return [];

  try {
    const params = { [`${searchType}[]`]: query };

    const response = await api.get<ExerciseResponse>("/exercises", { params });

    let resultList: string[] = [];

    switch (searchType) {
      case "name":
        resultList = response.data.data.map((exercise) => exercise.name);
        break;
      case "categoryName":
        resultList = response.data.data.flatMap((exercise) =>
          exercise.categories.map((c) => c.title)
        );
        break;
      case "programName":
        resultList = response.data.data.flatMap((exercise) =>
          exercise.programs.map((p) => p.title)
        );
        break;
      case "targetMuscleName":
        resultList = response.data.data.flatMap((exercise) =>
          exercise.primaryTargetMuscles.map((m) => m.name)
        );
        break;
      default:
        resultList = [];
    }

    return Array.from(new Set(resultList));
  } catch (error) {
    console.error(`Error searching ${searchType}:`, error);
    return [];
  }
};

export const searchExercisesId = async (
  searchType: "name",
  query: string
): Promise<{ id: number; name: string; exerciseType?: string }[]> => {
  if (!query.trim()) return [];

  try {
    const params = { [`${searchType}[]`]: query };

    const response = await api.get<ExerciseResponse>("/exercises", { params });

    const resultList = response.data.data.map((exercise) => {
      return { id: exercise.id, name: exercise.name, exerciseType: exercise.exerciseType };
    });

    return resultList;
  } catch (error) {
    console.error(`Error searching ${searchType}:`, error);
    return [];
  }
};

export const deleteExercise = async (id: number): Promise<void> => {
  try {
    const response = await api.delete(`/exercises/${id}`);

    if (response.status !== 200) {
      throw new Error("Failed to delete exercise");
    }
  } catch (error) {
    console.error("Error deleting exercise:", error);
    throw error;
  }
};

export const updateExercise = async (
  id: number,
  data: {
    name: string;
    description: string;
    imageUrls: string[];
    videoUrls: {
      url: string;
      thumbnail: string;
    }[];
    mood: string[];
    gender: string[];
    workingSets: {
      set: number;
      kg?: string;
      reps?: string;
      time?: string;
      meters?: string;
      kilometers?: string;
      minutes?: string;
    }[];
    fitnessLevel: string[];
    fitnessGoal: string[];
    priority: string;
    primaryTargetMuscles: number[];
    secondaryTargetMuscles: number[];
    programIds: number[];
    categoryIds: number[];
    equipmentIds: number[];
    howToPerformIds: number[];
  }
): Promise<Exercise> => {
  try {
    const response = await api.patch<Exercise>(`/exercises/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating exercise:", error);
    throw error;
  }
};

export const createExercise = async (data: {
  name: string;
  description: string;
  imageUrls: string[];
  videoUrls: {
    url: string;
    thumbnail: string;
  }[];
  workingSets: {
    set: number;
    kg?: string;
    reps?: string;
    time?: string;
    meters?: string;
    kilometers?: string;
    minutes?: string;
  }[];
  fitnessLevel: string[];
  fitnessGoal: string[];
  priority: string;
  exerciseType: ExerciseType;
  gender: string[];
  mood: string[];
  primaryTargetMuscles: number[];
  secondaryTargetMuscles: number[];
  programIds: number[];
  categoryIds: number[];
  equipmentIds: number[];
  howToPerformIds: number[];
}): Promise<Exercise> => {
  try {
    const response = await api.post<Exercise>("/exercises", data);
    return response.data;
  } catch (error) {
    console.error("Error creating exercise:", error);
    throw error;
  }
};

export interface TargetMuscle {
  id: number;
  name: string;
  imageUrl: string;
  darkImageUrl: string | null;
}

export const getTargetMuscles = async (): Promise<TargetMuscle[]> => {
  try {
    const response = await api.get<TargetMuscle[]>("/target-muscles");
    return response.data;
  } catch (error) {
    console.error("Error fetching target muscles:", error);
    throw error;
  }
};

export interface WorkoutCategory {
  id: number;
  title: string;
  imageUrl: string;
}

export const getWorkoutCategories = async (): Promise<WorkoutCategory[]> => {
  try {
    const response = await api.get<WorkoutCategory[]>("/workout-categories");
    return response.data;
  } catch (error) {
    console.error("Error fetching workout categories:", error);
    throw error;
  }
};

export const getExerciseById = async (id: number): Promise<Exercise> => {
  try {
    const response = await api.get<Exercise>(`/exercises/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching exercise:", error);
    throw error;
  }
};

export interface WorkoutProgram {
  id: number;
  title: string;
  imageUrl: string;
  trainerImage: string | null;
  trainer: string | null;
}

export const getWorkoutPrograms = async (): Promise<WorkoutProgram[]> => {
  try {
    const response = await api.get<WorkoutProgram[]>("/workout-programs");
    return response.data;
  } catch (error) {
    console.error("Error fetching workout programs:", error);
    throw error;
  }
};

export const uploadFile = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<{ url: string; thumbnail: string }> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post<{ url: string; thumbnail: string }>(
    "/upload",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 60000011111,
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          onProgress(percentCompleted);
        }
      },
    }
  );

  console.log("Uploaded file:", response.data);

  return response.data;
};

export const getExerciseSwaps = async (exerciseId: number): Promise<Exercise[]> => {
  const response = await api.get<Exercise[]>(`/exercises/${exerciseId}/swaps`);
  return response.data;
};

export const addExerciseSwap = async (exerciseId: number, swapId: number): Promise<Exercise[]> => {
  const response = await api.post<Exercise[]>(`/exercises/${exerciseId}/swaps`, { swapId });
  return response.data;
};

export const removeExerciseSwap = async (exerciseId: number, swapId: number): Promise<Exercise[]> => {
  const response = await api.delete<Exercise[]>(`/exercises/${exerciseId}/swaps/${swapId}`);
  return response.data;
};
